import React, { useState, useEffect } from "react";
import { Drawer, Steps, Button, Upload, Table, Select, Card, Typography, Space, message, Spin, Flex } from "antd";
import { DownloadOutlined, UploadOutlined, CheckCircleOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { useDispatch, useSelector } from "react-redux";
import { getData } from "../../actions/apiCall";
import type { UploadProps } from "antd/es/upload";
import { constants } from "../../constants/common";
import { postData } from "../../actions/developer";

const { Text, Title } = Typography;

interface BulkUploadModalProps {
    open: boolean;
    onCancel: () => void;
    onSuccess: () => void;
}

interface LeadRow {
    name: string;
    companyName: string;
    email: string;
    phone?: string;
    leadType: string;
    leadSource?: string;
    rowIndex: number;
    errors?: string[];
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({ open, onCancel, onSuccess }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [leads, setLeads] = useState<LeadRow[]>([]);
    const [validLeads, setValidLeads] = useState<LeadRow[]>([]);
    const [invalidLeads, setInvalidLeads] = useState<LeadRow[]>([]);
    const [selectedOwners, setSelectedOwners] = useState<string[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState<any>(null);

    const dispatch = useDispatch();
    const { user } = useSelector((app: any) => app?.app || {});
    const isAdmin = user?.designation === 'Admin';

    // Fetch users for owner dropdown (Admin only)
    useEffect(() => {
        if (open && isAdmin) {
            const fetchUsers = async () => {
                try {
                    const response = await dispatch(getData('/users'));
                    if (response && response.code === 200 && response.data) {
                        const usersData = Array.isArray(response.data) ? response.data : (response.data.data || []);
                        setUsers(usersData);
                    }
                } catch (error) {
                    console.error('Error fetching users:', error);
                }
            };
            fetchUsers();
        }
    }, [open, isAdmin, dispatch]);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!open) {
            setCurrentStep(0);
            setLeads([]);
            setValidLeads([]);
            setInvalidLeads([]);
            setSelectedOwners([]);
            setUploadResult(null);
        }
    }, [open]);

    // Download Excel template
    const downloadTemplate = () => {
        const templateData = [
            ['name', 'companyName', 'email', 'phone', 'leadType', 'leadSource'],
            ['John Doe', 'ABC Pvt Ltd', 'john@abc.com', '9876543210', 'Contracts', 'LinkedIn']
        ];

        const ws = XLSX.utils.aoa_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Leads Template');

        // Set column widths
        ws['!cols'] = [
            { wch: 20 }, // name
            { wch: 25 }, // companyName
            { wch: 30 }, // email
            { wch: 15 }, // phone
            { wch: 20 }, // leadType
            { wch: 20 }  // leadSource
        ];

        XLSX.writeFile(wb, 'leads_bulk_upload_template.xlsx');
    };

    // Validate lead row
    const validateLead = (row: any, rowIndex: number): { valid: boolean; errors: string[] } => {
        const errors: string[] = [];

        if (!row.name || !String(row.name).trim()) {
            errors.push('Name is required');
        }
        if (!row.companyName || !String(row.companyName).trim()) {
            errors.push('Company Name is required');
        }
        if (!row.email || !String(row.email).trim()) {
            errors.push('Email is required');
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(String(row.email).trim())) {
                errors.push('Invalid email format');
            }
        }
        if (!row.leadType || !String(row.leadType).trim()) {
            errors.push('Lead Type is required');
        }

        return {
            valid: errors.length === 0,
            errors
        };
    };

    // Parse uploaded file
    const parseFile = (file: File) => {
        const fileName = file.name.toLowerCase();
        const isExcelFile = fileName.endsWith('.xlsx') || fileName.endsWith('.xls') ||
            file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            file.type === 'application/vnd.ms-excel';
        const isCSVFile = fileName.endsWith('.csv') || file.type === 'text/csv';

        if (!isExcelFile && !isCSVFile) {
            message.error('Please upload a CSV or Excel file (.csv, .xlsx, or .xls)');
            return;
        }

        setLoading(true);

        const reader = new FileReader();

        if (isExcelFile) {
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e?.target?.result as ArrayBuffer);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                        header: 1,
                        defval: ''
                    });

                    if (jsonData.length < 2) {
                        message.warning('Excel file must have at least a header row and one data row');
                        setLoading(false);
                        return;
                    }

                    // Normalize headers
                    const headers = (jsonData[0] as any[]).map((h: any) => String(h || '').trim().toLowerCase());
                    const headerMap: { [key: string]: string } = {
                        'name': 'name',
                        'companyname': 'companyName',
                        'company name': 'companyName',
                        'company': 'companyName',
                        'email': 'email',
                        'phone': 'phone',
                        'phonenumber': 'phone',
                        'phone number': 'phone',
                        'leadtype': 'leadType',
                        'lead type': 'leadType',
                        'leadsource': 'leadSource',
                        'lead source': 'leadSource',
                        'source': 'leadSource'
                    };

                    const normalizedHeaders = headers.map(h => headerMap[h] || h);
                    const parsedLeads: LeadRow[] = [];

                    // Process data rows
                    for (let i = 1; i < jsonData.length; i++) {
                        const row = jsonData[i] as any[];
                        if (row.every(cell => !cell || String(cell).trim() === '')) continue; // Skip empty rows

                        const rowObj: any = { rowIndex: i + 1 };
                        normalizedHeaders.forEach((header, index) => {
                            if (header) {
                                rowObj[header] = row[index] !== undefined ? String(row[index] || '').trim() : '';
                            }
                        });

                        const validation = validateLead(rowObj, i + 1);
                        parsedLeads.push({
                            ...rowObj,
                            errors: validation.errors
                        });
                    }

                    processLeads(parsedLeads);
                } catch (error) {
                    console.error('Error parsing Excel:', error);
                    message.error('Error parsing Excel file');
                    setLoading(false);
                }
            };
            reader.readAsArrayBuffer(file);
        } else {
            // CSV parsing
            reader.onload = (e) => {
                try {
                    const text = e?.target?.result as string;
                    Papa.parse(text, {
                        header: true,
                        skipEmptyLines: true,
                        transformHeader: (header) => {
                            const lower = header.trim().toLowerCase();
                            const headerMap: { [key: string]: string } = {
                                'name': 'name',
                                'companyname': 'companyName',
                                'company name': 'companyName',
                                'company': 'companyName',
                                'email': 'email',
                                'phone': 'phone',
                                'phonenumber': 'phone',
                                'phone number': 'phone',
                                'leadtype': 'leadType',
                                'lead type': 'leadType',
                                'leadsource': 'leadSource',
                                'lead source': 'leadSource',
                                'source': 'leadSource'
                            };
                            return headerMap[lower] || header;
                        },
                        complete: (results) => {
                            const parsedLeads: LeadRow[] = results.data.map((row: any, index: number) => {
                                const validation = validateLead(row, index + 2); // +2 because header is row 1
                                return {
                                    ...row,
                                    rowIndex: index + 2,
                                    errors: validation.errors
                                };
                            });
                            processLeads(parsedLeads);
                        },
                        error: (error) => {
                            console.error('CSV parsing error:', error);
                            message.error('Error parsing CSV file');
                            setLoading(false);
                        }
                    });
                } catch (error) {
                    console.error('Error reading CSV:', error);
                    message.error('Error reading CSV file');
                    setLoading(false);
                }
            };
            reader.readAsText(file);
        }
    };

    const processLeads = (parsedLeads: LeadRow[]) => {
        const valid: LeadRow[] = [];
        const invalid: LeadRow[] = [];

        parsedLeads.forEach(lead => {
            if (lead.errors && lead.errors.length > 0) {
                invalid.push(lead);
            } else {
                valid.push(lead);
            }
        });

        setLeads(parsedLeads);
        setValidLeads(valid);
        setInvalidLeads(invalid);
        setCurrentStep(2);
        setLoading(false);

        if (valid.length === 0) {
            message.warning('No valid leads found in the file');
        } else {
            message.success(`Parsed ${valid.length} valid leads${invalid.length > 0 ? ` and ${invalid.length} invalid leads` : ''}`);
        }
    };

    const uploadProps: UploadProps = {
        beforeUpload: (file) => {
            parseFile(file);
            return false; // Prevent auto upload
        },
        accept: '.csv,.xlsx,.xls',
        showUploadList: false
    };

    // Handle bulk upload
    const handleUpload = async () => {
        if (validLeads.length === 0) {
            message.error('No valid leads to upload');
            return;
        }

        setUploading(true);

        try {
            // Determine owner IDs
            let ownerIds: string[] = [];
            if (isAdmin && selectedOwners.length > 0) {
                ownerIds = selectedOwners;
            } else {
                // Non-admin or no owners selected - assign to current user
                ownerIds = [user._id];
            }

            // Prepare payload
            const leadsPayload = validLeads.map(lead => ({
                name: lead.name,
                companyName: lead.companyName,
                email: lead.email,
                phone: lead.phone || '',
                leadType: lead.leadType,
                leadSource: lead.leadSource || ''
            }));

            const response = await dispatch(postData('/leads/bulk-upload', {
                leads: leadsPayload,
                ownerIds: ownerIds
            }));

            if (response && response.code === 200) {
                setUploadResult(response.data);
                setCurrentStep(3);
                message.success(`Successfully uploaded ${response.data.created} leads`);

                // Trigger table refresh
                dispatch({
                    type: 'REFRESH_TABLE',
                    refreshTable: true
                });

                setTimeout(() => {
                    onSuccess();
                    onCancel();
                }, 2000);
            } else {
                message.error(response?.message || 'Failed to upload leads');
            }
        } catch (error) {
            console.error('Upload error:', error);
            message.error('Error uploading leads');
        } finally {
            setUploading(false);
        }
    };

    const errorColumns = [
        {
            title: 'Row',
            dataIndex: 'rowIndex',
            key: 'rowIndex',
            width: 80
        },
        {
            title: 'Error',
            dataIndex: 'errors',
            key: 'errors',
            render: (errors: string[]) => (
                <div>
                    {errors.map((error, idx) => (
                        <div key={idx} style={{ color: '#ff4d4f' }}>
                            {error}
                        </div>
                    ))}
                </div>
            )
        }
    ];

    const steps = [
        {
            title: 'Download Template',
            content: (
                <div style={{ padding: '10px' }}>
                    <div style={{ textAlign: 'center', marginBottom: 32 }}>
                        <Title level={4} style={{ marginBottom: 12 }}>Download Excel Template</Title>
                        <Text type="secondary" style={{ display: 'block', fontSize: 14 }}>
                            Download the template file and fill in the lead information. Fields marked with * are mandatory.
                        </Text>
                    </div>
                    <div style={{ textAlign: 'center', marginBottom: 32 }}>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            size="small"
                            onClick={downloadTemplate}
                            style={{ height: 40, paddingLeft: 24, paddingRight: 24 }}
                        >
                            Download Excel Template
                        </Button>
                    </div>
                    <Card style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                        <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 12 }}>Template Columns:</Text>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            <li style={{ marginBottom: 8 }}><Text>name * (e.g., John Doe)</Text></li>
                            <li style={{ marginBottom: 8 }}><Text>companyName * (e.g., ABC Pvt Ltd)</Text></li>
                            <li style={{ marginBottom: 8 }}><Text>email * (e.g., john@abc.com)</Text></li>
                            <li style={{ marginBottom: 8 }}><Text>phone (e.g., 9876543210)</Text></li>
                            <li style={{ marginBottom: 8 }}><Text>leadType * (e.g., Contracts)</Text></li>
                            <li><Text>leadSource (e.g., LinkedIn)</Text></li>
                        </ul>
                    </Card>
                </div>
            )
        },
        {
            title: 'Upload Excel',
            content: (
                <div style={{ padding: '24px', backgroundColor: '#FFFFFF', borderRadius: '8px' }}>
                    <Title level={4} style={{ marginBottom: 12 }}>Upload Excel File</Title>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 24, fontSize: 14 }}>
                        Upload your filled Excel or CSV file (.xlsx, .xls, or .csv)
                    </Text>
                    <div style={{ textAlign: 'center', padding: '40px 20px', border: '2px dashed #D1D5DB', borderRadius: '8px', backgroundColor: '#F9FAFB' }}>
                        <Upload {...uploadProps}>
                            <Button icon={<UploadOutlined />} size="small" loading={loading} style={{ height: 40 }}>
                                Click to Upload
                            </Button>
                        </Upload>
                        {loading && (
                            <div style={{ marginTop: 16 }}>
                                <Spin /> <Text style={{ marginLeft: 8 }}>Parsing file...</Text>
                            </div>
                        )}
                    </div>
                </div>
            )
        },
        {
            title: 'Review & Assign',
            content: (
                <div style={{ padding: '24px', backgroundColor: '#FFFFFF', borderRadius: '8px' }}>
                    <Card style={{ marginBottom: 16, backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                        <Flex justify="space-between" wrap="wrap" gap={16}>
                            <div>
                                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Total Leads</Text>
                                <Text strong style={{ fontSize: 20 }}>{leads.length}</Text>
                            </div>
                            <div>
                                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Valid Leads</Text>
                                <Text strong style={{ fontSize: 20, color: '#52c41a' }}>{validLeads.length}</Text>
                            </div>
                            <div>
                                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Invalid Leads</Text>
                                <Text strong style={{ fontSize: 20, color: '#ff4d4f' }}>{invalidLeads.length}</Text>
                            </div>
                        </Flex>
                    </Card>

                    {invalidLeads.length > 0 && (
                        <Card
                            title={<Text strong>Validation Errors</Text>}
                            style={{ marginBottom: 16, border: '1px solid #FEE2E2', backgroundColor: '#FEF2F2' }}
                        >
                            <Table
                                dataSource={invalidLeads}
                                columns={errorColumns}
                                pagination={{ pageSize: 5 }}
                                size="small"
                                rowKey="rowIndex"
                            />
                        </Card>
                    )}

                    {validLeads.length > 0 && (
                        <>
                            {isAdmin && (
                                <Card
                                    title={<Text strong>Assign Leads To</Text>}
                                    style={{ marginBottom: 16, border: '1px solid #E5E7EB' }}
                                >
                                    <Select
                                        mode="multiple"
                                        style={{ width: '100%' }}
                                        placeholder="Select owners (leads will be evenly distributed)"
                                        value={selectedOwners}
                                        onChange={setSelectedOwners}
                                        showSearch
                                        size="middle"
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={users.map(user => ({
                                            value: user._id,
                                            label: `${user.firstName} ${user.lastName}`,
                                            user: user
                                        }))}
                                    />
                                    {selectedOwners.length > 0 && (
                                        <div style={{ marginTop: 16 }}>
                                            <Text type="secondary" style={{ fontSize: 13 }}>
                                                Leads will be evenly distributed among {selectedOwners.length} owner(s)
                                            </Text>
                                        </div>
                                    )}
                                </Card>
                            )}

                            {!isAdmin && (
                                <Card style={{ marginBottom: 16, backgroundColor: '#F0F9FF', border: '1px solid #BAE6FD' }}>
                                    <Text>
                                        Leads will be assigned to: <Text strong>{user?.firstName} {user?.lastName}</Text>
                                    </Text>
                                </Card>
                            )}

                            <Card title={<Text strong>Summary</Text>} style={{ border: '1px solid #E5E7EB' }}>
                                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                    <Flex justify="space-between">
                                        <Text>Total Leads:</Text>
                                        <Text strong>{leads.length}</Text>
                                    </Flex>
                                    <Flex justify="space-between">
                                        <Text>Valid Leads:</Text>
                                        <Text strong style={{ color: '#52c41a' }}>{validLeads.length}</Text>
                                    </Flex>
                                    <Flex justify="space-between">
                                        <Text>Invalid Leads:</Text>
                                        <Text strong style={{ color: '#ff4d4f' }}>{invalidLeads.length}</Text>
                                    </Flex>
                                    <Flex justify="space-between">
                                        <Text>Owners Selected:</Text>
                                        <Text strong>{isAdmin ? (selectedOwners.length || 1) : 1}</Text>
                                    </Flex>
                                </Space>
                            </Card>
                        </>
                    )}
                </div>
            )
        },
        {
            title: 'Complete',
            content: (
                <div style={{ padding: '60px 20px', textAlign: 'center', backgroundColor: '#FFFFFF', borderRadius: '8px' }}>
                    <CheckCircleOutlined style={{ fontSize: 72, color: '#52c41a', marginBottom: 24 }} />
                    <Title level={3} style={{ color: '#52c41a', marginBottom: 32 }}>Leads Uploaded Successfully</Title>
                    {uploadResult && (
                        <Card style={{ maxWidth: 400, margin: '0 auto', backgroundColor: '#F0FDF4', border: '1px solid #86EFAC' }}>
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <div>
                                    <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Leads Created</Text>
                                    <Text strong style={{ fontSize: 24, color: '#52c41a' }}>
                                        {uploadResult.created}
                                    </Text>
                                </div>
                                <div>
                                    <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Tasks Assigned</Text>
                                    <Text strong style={{ fontSize: 24, color: '#52c41a' }}>
                                        {uploadResult.tasksCreated}
                                    </Text>
                                </div>
                            </Space>
                        </Card>
                    )}
                </div>
            )
        }
    ];

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const renderFooter = () => {
        return (
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                {currentStep > 0 && currentStep < 3 && (
                    <Button icon={<ArrowLeftOutlined />} onClick={handlePrev}>
                        Previous
                    </Button>
                )}
                <Button onClick={onCancel}>
                    {currentStep === 3 ? 'Close' : 'Cancel'}
                </Button>
                {currentStep === 0 && (
                    <Button type="primary" onClick={() => setCurrentStep(1)}>
                        Next
                    </Button>
                )}
                {currentStep === 1 && validLeads.length > 0 && (
                    <Button type="primary" onClick={() => setCurrentStep(2)}>
                        Next
                    </Button>
                )}
                {currentStep === 2 && validLeads.length > 0 && (
                    <Button
                        type="primary"
                        loading={uploading}
                        onClick={handleUpload}
                        disabled={isAdmin && selectedOwners.length === 0}
                    >
                        Upload Leads
                    </Button>
                )}
            </Space>
        );
    };

    return (
        <Drawer
            open={open}
            onClose={onCancel}
            title={
                <div>
                    <Title level={4} style={{ margin: 0 }}>Bulk Lead Upload</Title>
                </div>
            }
            width="40%"
            placement="right"
            destroyOnClose={true}
            footer={renderFooter()}
            footerStyle={{ textAlign: 'end', padding: '12px 24px', borderTop: '1px solid #E5E7EB' }}
            bodyStyle={{
                backgroundColor: '#eef1f9',
                padding: '24px',
                overflowY: 'auto'
            }}
            headerStyle={{
                backgroundColor: '#FFFFFF',
                padding: '16px 24px',
                borderBottom: '1px solid #E5E7EB'
            }}
        >
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                <Steps
                    current={currentStep}
                    style={{ marginBottom: 32 }}
                    items={steps.map((step, index) => ({
                        title: step.title,
                        key: index
                    }))}
                />
                <div style={{ minHeight: 400 }}>
                    {steps[currentStep].content}
                </div>
            </div>
        </Drawer>
    );
};

export default BulkUploadModal;
